import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ============================================
// TYPES
// ============================================

export type NoteCategory = 'general' | 'meeting' | 'task' | 'idea' | 'issue' | 'decision';
export type NotePriority = 'low' | 'medium' | 'high';

export interface Note {
  id: string;
  title: string;
  content: string; // Rich text HTML
  category: NoteCategory;
  priority: NotePriority;
  tags: string[];
  isPinned: boolean;
  isArchived: boolean;

  // Related record (optional)
  relatedTo?: {
    objectType: string; // e.g., 'User', 'Client', 'Case'
    recordId: string;
    recordName: string;
  };

  // Attachments (simulated)
  attachments: Array<{
    id: string;
    name: string;
    size: number; // in bytes
    type: string; // mime type
    uploadedDate: string;
  }>;

  // Metadata
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
}

// ============================================
// MOCK DATA
// ============================================

export const mockNotes: Note[] = [
  {
    id: 'note_001',
    title: 'Q1 Planning Meeting Notes',
    content: `<h2>Q1 Planning Meeting</h2>
<p><strong>Date:</strong> January 15, 2025</p>
<p><strong>Attendees:</strong> John, Sarah, Mike, Lisa</p>

<h3>Key Discussion Points:</h3>
<ul>
  <li>New feature rollout timeline</li>
  <li>Budget allocation for Q1</li>
  <li>Team expansion plans</li>
</ul>

<h3>Action Items:</h3>
<ol>
  <li>John to finalize budget proposal by Jan 20</li>
  <li>Sarah to schedule follow-up meeting</li>
  <li>Mike to review technical requirements</li>
</ol>

<p><em>Next meeting scheduled for January 30, 2025</em></p>`,
    category: 'meeting',
    priority: 'high',
    tags: ['planning', 'Q1', 'budget', 'features'],
    isPinned: true,
    isArchived: false,
    relatedTo: undefined,
    attachments: [
      {
        id: 'att_001',
        name: 'Q1_Budget_Draft.pdf',
        size: 245000,
        type: 'application/pdf',
        uploadedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
      }
    ],
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin'
  },
  {
    id: 'note_002',
    title: 'Bug Fix Priority List',
    content: `<h2>Critical Bugs to Address</h2>

<h3>High Priority:</h3>
<ul>
  <li><strong>Login timeout issue</strong> - Users being logged out randomly</li>
  <li><strong>Email notifications</strong> - Not sending consistently</li>
  <li><strong>Dashboard loading</strong> - Slow performance on mobile</li>
</ul>

<h3>Medium Priority:</h3>
<ul>
  <li>Search filter not working on user page</li>
  <li>Dark mode color contrast issues</li>
  <li>Export CSV button alignment</li>
</ul>

<p><strong>Target completion:</strong> End of January</p>`,
    category: 'issue',
    priority: 'high',
    tags: ['bugs', 'development', 'urgent'],
    isPinned: true,
    isArchived: false,
    relatedTo: undefined,
    attachments: [],
    createdBy: 'Developer',
    createdDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Developer'
  },
  {
    id: 'note_003',
    title: 'New Feature Ideas',
    content: `<h2>Feature Brainstorming Session</h2>

<h3>User Requests:</h3>
<ul>
  <li>Bulk actions for multiple records</li>
  <li>Advanced filtering options</li>
  <li>Mobile app improvements</li>
  <li>Integration with third-party tools</li>
</ul>

<h3>Team Suggestions:</h3>
<ul>
  <li>AI-powered search</li>
  <li>Customizable dashboards</li>
  <li>Automated workflows</li>
  <li>Real-time collaboration</li>
</ul>

<p><strong>Next Steps:</strong> Prioritize and create tickets for top 3 features</p>`,
    category: 'idea',
    priority: 'medium',
    tags: ['features', 'brainstorm', 'roadmap'],
    isPinned: false,
    isArchived: false,
    relatedTo: undefined,
    attachments: [],
    createdBy: 'Product Manager',
    createdDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Product Manager'
  },
  {
    id: 'note_004',
    title: 'Client Onboarding Checklist',
    content: `<h2>Standard Client Onboarding Process</h2>

<h3>Pre-Onboarding:</h3>
<ol>
  <li>Send welcome email</li>
  <li>Schedule kickoff call</li>
  <li>Prepare account credentials</li>
</ol>

<h3>During Onboarding:</h3>
<ol>
  <li>System walkthrough</li>
  <li>Configure user settings</li>
  <li>Import existing data</li>
  <li>Set up integrations</li>
</ol>

<h3>Post-Onboarding:</h3>
<ol>
  <li>Follow-up email with resources</li>
  <li>Schedule 30-day check-in</li>
  <li>Collect feedback</li>
</ol>`,
    category: 'task',
    priority: 'medium',
    tags: ['onboarding', 'clients', 'process'],
    isPinned: false,
    isArchived: false,
    relatedTo: {
      objectType: 'Client',
      recordId: 'client_001',
      recordName: 'Acme Corporation'
    },
    attachments: [
      {
        id: 'att_002',
        name: 'Onboarding_Checklist.docx',
        size: 125000,
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        uploadedDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString()
      }
    ],
    createdBy: 'Sales Team',
    createdDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Sales Team'
  },
  {
    id: 'note_005',
    title: 'Security Audit Findings',
    content: `<h2>Q4 Security Audit Results</h2>

<p><strong>Audit Date:</strong> December 2024</p>
<p><strong>Auditor:</strong> SecureIT Solutions</p>

<h3>Findings:</h3>
<ul>
  <li>✅ Password policies meet industry standards</li>
  <li>✅ SSL certificates properly configured</li>
  <li>⚠️ MFA adoption at 65% - target is 90%</li>
  <li>⚠️ Some API endpoints missing rate limiting</li>
</ul>

<h3>Recommendations:</h3>
<ol>
  <li>Implement company-wide MFA mandate</li>
  <li>Add rate limiting to all public APIs</li>
  <li>Schedule quarterly security reviews</li>
</ol>`,
    category: 'decision',
    priority: 'high',
    tags: ['security', 'audit', 'compliance'],
    isPinned: false,
    isArchived: false,
    relatedTo: undefined,
    attachments: [
      {
        id: 'att_003',
        name: 'Security_Audit_Report_Q4.pdf',
        size: 580000,
        type: 'application/pdf',
        uploadedDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString()
      }
    ],
    createdBy: 'Security Team',
    createdDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Security Team'
  },
  {
    id: 'note_006',
    title: 'General Reminders',
    content: `<h2>Miscellaneous Notes</h2>

<ul>
  <li>Office closed on January 20 (holiday)</li>
  <li>Team lunch scheduled for Friday at noon</li>
  <li>New parking permits available at reception</li>
  <li>IT maintenance window: Sunday 2-4 AM</li>
</ul>`,
    category: 'general',
    priority: 'low',
    tags: ['reminders', 'announcements'],
    isPinned: false,
    isArchived: false,
    relatedTo: undefined,
    attachments: [],
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin'
  },
  {
    id: 'note_007',
    title: 'Archived: Old Project Notes',
    content: `<p>This note has been archived and contains historical project information.</p>`,
    category: 'general',
    priority: 'low',
    tags: ['archived', 'old'],
    isPinned: false,
    isArchived: true,
    relatedTo: undefined,
    attachments: [],
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin'
  }
];

// ============================================
// QUERY KEYS
// ============================================

export const noteKeys = {
  all: ['notes'] as const,
  lists: () => [...noteKeys.all, 'list'] as const,
  list: (filters: string) => [...noteKeys.lists(), filters] as const,
  details: () => [...noteKeys.all, 'detail'] as const,
  detail: (id: string) => [...noteKeys.details(), id] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all notes with optional filtering
 * Future: Replace with API call to GET /api/notes
 */
export function useNotes(filters?: {
  searchQuery?: string;
  category?: NoteCategory | 'all';
  priority?: NotePriority | 'all';
  tags?: string[];
  isPinned?: boolean;
  isArchived?: boolean;
}) {
  return useQuery({
    queryKey: noteKeys.list(JSON.stringify(filters || {})),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 400));

      let notes = [...mockNotes];

      // Apply filters
      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        notes = notes.filter(n =>
          n.title.toLowerCase().includes(query) ||
          n.content.toLowerCase().includes(query) ||
          n.tags.some(tag => tag.toLowerCase().includes(query))
        );
      }

      if (filters?.category && filters.category !== 'all') {
        notes = notes.filter(n => n.category === filters.category);
      }

      if (filters?.priority && filters.priority !== 'all') {
        notes = notes.filter(n => n.priority === filters.priority);
      }

      if (filters?.tags && filters.tags.length > 0) {
        notes = notes.filter(n =>
          filters.tags!.some(tag => n.tags.includes(tag))
        );
      }

      if (filters?.isPinned !== undefined) {
        notes = notes.filter(n => n.isPinned === filters.isPinned);
      }

      if (filters?.isArchived !== undefined) {
        notes = notes.filter(n => n.isArchived === filters.isArchived);
      }

      // Sort: pinned first, then by last modified
      return notes.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
      });
    },
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Get single note
 * Future: Replace with API call to GET /api/notes/:id
 */
export function useNote(noteId: string) {
  return useQuery({
    queryKey: noteKeys.detail(noteId),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const note = mockNotes.find(n => n.id === noteId);
      if (!note) {
        throw new Error('Note not found');
      }

      return note;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!noteId,
  });
}

/**
 * Create new note
 * Future: Replace with API call to POST /api/notes
 */
export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteData: Omit<Note, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>) => {
      await new Promise(resolve => setTimeout(resolve, 600));

      const newNote: Note = {
        ...noteData,
        id: `note_${Date.now()}`,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User'
      };

      mockNotes.push(newNote);
      return newNote;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Update note
 * Future: Replace with API call to PATCH /api/notes/:id
 */
export function useUpdateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, updates }: { noteId: string; updates: Partial<Note> }) => {
      await new Promise(resolve => setTimeout(resolve, 500));

      const index = mockNotes.findIndex(n => n.id === noteId);
      if (index === -1) {
        throw new Error('Note not found');
      }

      mockNotes[index] = {
        ...mockNotes[index],
        ...updates,
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User'
      };

      return mockNotes[index];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: noteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: noteKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Delete note
 * Future: Replace with API call to DELETE /api/notes/:id
 */
export function useDeleteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      await new Promise(resolve => setTimeout(resolve, 400));

      const index = mockNotes.findIndex(n => n.id === noteId);
      if (index === -1) {
        throw new Error('Note not found');
      }

      mockNotes.splice(index, 1);
      return { noteId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Toggle note pin status
 * Future: Replace with API call to PATCH /api/notes/:id/pin
 */
export function useTogglePinNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const note = mockNotes.find(n => n.id === noteId);
      if (!note) {
        throw new Error('Note not found');
      }

      note.isPinned = !note.isPinned;
      note.lastModified = new Date().toISOString();
      note.lastModifiedBy = 'Current User';

      return note;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.lists() });
    },
  });
}

/**
 * Archive/unarchive note
 * Future: Replace with API call to PATCH /api/notes/:id/archive
 */
export function useArchiveNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, archive }: { noteId: string; archive: boolean }) => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const note = mockNotes.find(n => n.id === noteId);
      if (!note) {
        throw new Error('Note not found');
      }

      note.isArchived = archive;
      note.lastModified = new Date().toISOString();
      note.lastModifiedBy = 'Current User';

      return note;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.lists() });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get category label
 */
export function getCategoryLabel(category: NoteCategory): string {
  const labels: Record<NoteCategory, string> = {
    general: 'General',
    meeting: 'Meeting',
    task: 'Task',
    idea: 'Idea',
    issue: 'Issue',
    decision: 'Decision'
  };
  return labels[category];
}

/**
 * Get category color
 */
export function getCategoryColor(category: NoteCategory): string {
  const colors: Record<NoteCategory, string> = {
    general: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    meeting: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
    task: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
    idea: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400',
    issue: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
    decision: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
  };
  return colors[category];
}

/**
 * Get priority label
 */
export function getPriorityLabel(priority: NotePriority): string {
  const labels: Record<NotePriority, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High'
  };
  return labels[priority];
}

/**
 * Get priority color
 */
export function getPriorityColor(priority: NotePriority): string {
  const colors: Record<NotePriority, string> = {
    low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
    high: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
  };
  return colors[priority];
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get all unique tags from notes
 */
export function getAllTags(): string[] {
  const tagsSet = new Set<string>();
  mockNotes.forEach(note => {
    note.tags.forEach(tag => tagsSet.add(tag));
  });
  return Array.from(tagsSet).sort();
}

/**
 * Strip HTML tags from content
 */
export function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}
